import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandL } from '@sorare/core/src/atoms/typography';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Section, StyledContainer, Title } from '#src/Careers/ui.tsx';

const TitleWrapper = styled.div`
  @media ${laptopAndAbove} {
    width: 30%;
  }
`;

const List = styled.ul`
  padding-left: 1em; /** for list-style-type */
  margin: 0;
  li {
    list-style-type: disc;
  }
`;

export const Values = () => {
  return (
    <Section $spacing>
      <StyledContainer>
        <Title>Our Values</Title>
        <Vertical gap={2}>
          <Vertical>
            <TitleWrapper>
              <BrandL as="h3">Own Your Game</BrandL>
            </TitleWrapper>
            <BodyM bold>
              We are a collective of entrepreneurs who move fast, craft quality
              outcomes, and strive to grow every day as individuals and
              professionals.
            </BodyM>
            <List>
              <li>
                <BodyM bold>Intensity.</BodyM>
                <BodyM>
                  You accomplish significant amounts of important work, at a
                  fast pace. Don’t get the “illusion of work” by spreading
                  yourself too thin into tasks that should be automated by AI,
                  or B Problems that won’t move the needle. Obsess over A
                  problems that will change the trajectory of the company.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Bias to action.</BodyM>
                <BodyM>
                  You&apos;re not the person who lists obstacles. You&apos;re
                  the person who says &quote;let&apos;s do it and learn along
                  the way.&quote; No analysis paralysis, even on hard decisions.
                  You move. Action produces information. Don’t follow the
                  startup cliché of &quote;move fast and break things.&quote; We
                  move with urgency AND precision. Speed without quality is
                  waste.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Self-starter.</BodyM>
                <BodyM>
                  You don&apos;t wait for someone to teach you. You take
                  initiative. You&apos;re in the driver&apos;s seat. Don’t take
                  it as a mandate to start initiatives that can be (i)
                  disconnected from the company or your team’s goal (ii)
                  overlapping with other teams initiatives.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Judgment.</BodyM>
                <BodyM>
                  You&apos;re known for making smart decisions under ambiguity.
                  On people, technology, business, or creative fronts.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Humility.</BodyM>
                <BodyM>
                  You admit mistakes fast. You&apos;re not afraid to make them
                  and turn them into lessons. You know it’s OK to change your
                  mind to get to the best outcomes for Sorare.
                </BodyM>
              </li>
            </List>
          </Vertical>
          <Vertical>
            <TitleWrapper>
              <BrandL as="h3">Build with the fans, and for the fans</BrandL>
            </TitleWrapper>
            <BodyM bold>
              We are passionate about creating simple, delightful and trusted
              experiences for the sports community.
            </BodyM>
            <List>
              <li>
                <BodyM bold>Use the product daily.</BodyM>
                <BodyM>
                  You know what our fans want because you use Sorare every day,
                  with passion. You test our products like a real Manager. Don’t
                  keep your feedback for yourself or some of your colleagues.
                  Share everything publicly in the dedicated channels, in a
                  constructive way.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Build trust.</BodyM>
                <BodyM>
                  You build trust with our managers through reactivity,
                  transparency, and authenticity. If we say we&apos;ll do
                  something, we do it. If we&apos;re wrong, we say so first,
                  then fix it. Don’t consider or say that “the community
                  doesn&apos;t get it, or this user did not take the time to
                  read that”. It’s on us.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Simplicity everywhere.</BodyM>
                <BodyM>
                  Fewer words. Fewer screens. Fewer clicks. In communications
                  and in experiences. Big, simple, lively. Don’t conflate
                  simplicity of our UX / UI and gameplay depth. Gameplay depth
                  is OK as long as you introduce new features at the right time
                  in the user journey, the right way.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Obsess over details.</BodyM>
                <BodyM>
                  Don’t tolerate mediocrity. An experience is made by the
                  thousands of details from our craft. Every pixel and word that
                  goes to the external world. The moment we lower the bar
                  somewhere, we don’t respect the fans anymore.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Know the strategy.</BodyM>
                <BodyM>
                  You understand our strategy and market. You can articulate the
                  company&apos;s priorities at any point in time. You can link
                  your day to day to your individual, team and company goals.
                  Don’t be obsessed by what our “competitors” are doing. You can
                  be inspired by part of their execution. But think from first
                  principles about what’s best for our company by putting our
                  fans above all.
                </BodyM>
              </li>
            </List>
          </Vertical>
          <Vertical>
            <TitleWrapper>
              <BrandL as="h3">Win as a Team</BrandL>
            </TitleWrapper>
            <BodyM bold>
              What&apos;s best for Sorare comes before what&apos;s best for you.
            </BodyM>
            <List>
              <li>
                <BodyM bold>Team first.</BodyM>
                <BodyM>
                  Your personal success comes after collective success. Not the
                  other way around.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Constructive.</BodyM>
                <BodyM>
                  You&apos;re not political. When you disagree, it&apos;s about
                  the idea, not the person or your department&apos;s turf. A
                  decision is made? You commit to it, even if you disagree.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Direct.</BodyM>
                <BodyM>
                  Concise. Respectful. No detours. No unspoken messages. Don’t
                  be direct without being kind and respectful.
                </BodyM>
              </li>
              <li>
                <BodyM bold>Courage.</BodyM>
                <BodyM>
                  You say what you think, even if it&apos;s controversial. And
                  you say it to the person&apos;s face, not at the coffee
                  machine.
                </BodyM>
              </li>
            </List>
          </Vertical>
        </Vertical>
      </StyledContainer>
    </Section>
  );
};
